import React from 'react';
import { drawnFileSrc, drawnRemoteSrc } from '../../../common/drawnImageSrc';
import { inlineMediaUrl } from '../inlineMediaUrl';
import type { CalendarPost, CalendarSong } from '../types';

const TITLE_WORDS = 5;

/** The size of a cover on a Calendar card, in CSS px (the day panel draws the largest). */
const CALENDAR_COVER_PX = 96;

/**
 * The cover of a song: our stored file first, else the cover address of the claim. Both through
 * imgproxy, sized for a Calendar card (owner rule 2026-10-19).
 */
export function songArtworkUrl(song: CalendarSong | undefined): string | null {
  if (song === undefined) {
    return null;
  }
  if (song.artworkFile !== null) {
    return song.artworkFile.sourceUrl === undefined
      ? inlineMediaUrl(song.artworkFile.url)
      : drawnFileSrc(song.artworkFile, CALENDAR_COVER_PX);
  }
  return song.artworkUrl === null ? null : drawnRemoteSrc(song.artworkUrl, CALENDAR_COVER_PX);
}

/**
 * The picture of a post card: the poster of a video, the picture of an image post, else the song
 * cover. Null: a text post ("Aa").
 */
export function postThumbUrl(post: CalendarPost, song: CalendarSong | undefined): string | null {
  if (post.posterUrl !== null) {
    return inlineMediaUrl(post.posterUrl);
  }
  if (post.mediaKind === 'image' && post.mediaFileUrl !== null) {
    return inlineMediaUrl(post.mediaFileUrl);
  }
  if (post.mediaFileId !== null || post.catalogItemUuid !== null) {
    return songArtworkUrl(song);
  }
  return null;
}

/** The name of a post on a card: the song title, else the first words of the caption. */
export function postTitle(post: CalendarPost, song: CalendarSong | undefined): string {
  if (song !== undefined) {
    return song.title;
  }
  return post.caption.trim().split(/\s+/).slice(0, TITLE_WORDS).join(' ');
}

/**
 * The song colour as the CSS variable `--song` (the accent of the release theme). With no kit
 * the variable is not set and the styles fall back to the brand violet.
 */
export function songColourStyle(song: CalendarSong | undefined): React.CSSProperties | undefined {
  if (song?.accentColour === null || song?.accentColour === undefined) {
    return undefined;
  }
  return { '--song': song.accentColour } as React.CSSProperties;
}

/** `13:00` in the 24-hour clock of the locale. */
export function formatPostTime(formatTime: (value: Date, options: Intl.DateTimeFormatOptions) => string, at: Date) {
  return formatTime(at, { hour: '2-digit', minute: '2-digit', hour12: false });
}
